<template>
  <v-card
    elevation="0"
    outlined
  >
    <v-card-title>
      <div>Traffic Allocation (%) for A/B Test Strategies</div>

      <v-spacer />

      <template v-if="abTestId">
        <v-btn
          class="btn-bg-deep-blue"
          @click="openStrategyRoutingTester = true"
        >
          Test Strategy Allocation
        </v-btn>
        <AbTestStrategyRoutingTester
          :is-open="openStrategyRoutingTester"
          :ab-test-id="abTestId"
          @close="openStrategyRoutingTester = false"
        />
      </template>
    </v-card-title>

    <v-card-text v-if="!championStrategy">
      <v-alert
        class="my-10 mx-5"
        outlined
        prominent
        text
        type="warning"
      >
        To configure strategy distributions,
        first select a best lending strategy in the section above. Strategy
        distribution settings will be available once a strategy is selected.
      </v-alert>
    </v-card-text>

    <template v-else>
      <v-card-text>
        <v-overlay
          :model-value="isLoading"
          absolute
        >
          <v-progress-circular
            indeterminate
            size="64"
          />
        </v-overlay>
        <v-table fixed-header>
          <caption>
            <v-alert
              style="width: 100%;"
              outlined
              class="mt-2 mb-2"
              text
              type="info"
            >
              Any Strategy created here (except the default strategy)
              will be saved as a new lending strategy in the system.
              Please ensure that the strategy name is unique.
            </v-alert>
          </caption>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Traffic %</th>
              <th>Scoring Model</th>
              <th>Product Assignment Models</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in trafficAllocations"
              :key="`allocation-${index}-${row.strategyId ?? 'new'}`"
            >
              <td>
                <v-text-field
                  v-model="trafficAllocations[index].strategyName"
                  dense
                  style="min-width: 120px"
                  placeholder="Strategy Name"
                  :error-messages="getFieldError('strategyName', index)"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                />
              </td>

              <td>
                <v-textarea
                  v-model="trafficAllocations[index].strategyDescription"
                  style="min-width: 320px"
                  rows="2"
                  placeholder="Strategy Description"
                  auto-grow
                  :error-messages="getFieldError('strategyDescription', index)"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                />
              </td>

              <td>
                <v-text-field
                  v-model.number="trafficAllocations[index].allocationPct"
                  style="width: 84px"
                  dense
                  type="number"
                  min="0"
                  max="100"
                  :error-messages="getFieldError('allocationPct', index)"
                />
              </td>

              <td>
                <v-select
                  v-model="trafficAllocations[index].scoringModelId"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                  :items="scoringModels"
                  placeholder="Scoring Model"
                  item-title="name"
                  item-value="id"
                  required
                  dense
                  :error-messages="getFieldError('scoringModelId', index)"
                />
              </td>

              <td>
                <v-select
                  v-model="trafficAllocations[index].productAssignmentModelId"
                  :disabled="isReadOnlyStrategy(trafficAllocations[index])"
                  :items="productAssignmentModels"
                  placeholder="Product Assignment Models"
                  item-title="name"
                  item-value="id"
                  required
                  dense
                  :error-messages="getFieldError('productAssignmentModelId', index)"
                />
              </td>

              <td class="text-right">
                <v-btn
                  v-if="canBeDeleted(index)"
                  color="error"
                  icon
                  small
                  @click="removeStrategy(index)"
                >
                  <v-icon> mdi-trash-can-outline </v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td
                colspan="6"
                class="text-right"
              >
                <v-btn
                  color="primary"
                  :disabled="isLoading"
                  small
                  @click="addStrategy"
                >
                  <v-icon> mdi-plus </v-icon>
                  Add Strategy
                </v-btn>
              </td>
            </tr>
          </tfoot>
        </v-table>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { unref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import AbTestStrategyRoutingTester from './AbTestStrategyRoutingTester.vue';

export default {
  name: 'AbTestTrafficAllocations',

  components: {
    AbTestStrategyRoutingTester,
  },

  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    championStrategy: {
      type: Object,
      default: null,
    },

    abTestId: {
      type: Number,
      default: null,
    },

    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'valid-traffic-allocations'],

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      trafficAllocations: {
        $each: helpers.forEach({
          strategyName: {
            required,
          },
          strategyDescription: {
            required,
          },
          isChampionStrategy: {
            required,
          },
          allocationPct: {
            required,
          },
          scoringModelId: {
            required,
          },
          productAssignmentModelId: {
            required,
          },
        }),
      },
    };
  },

  data() {
    return {
      isLoading: false,
      trafficAllocations: [],

      scoringModels: [],
      productAssignmentModels: [],
      validationErrors: {},

      openStrategyRoutingTester: false,
    };
  },

  watch: {
    trafficAllocations: {
      handler(newAllocations) {
        this.v$.$touch();

        this.$emit('valid-traffic-allocations', this.v$.$invalid === false);
        this.$emit('update:modelValue', newAllocations);
      },
      deep: true,
    },

    modelValue: {
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.trafficAllocations)) {
          this.trafficAllocations = newValue?.length ? [...newValue] : [];
          this.setDefaultStrategyDistributions();
          this.$nextTick(() => {
            this.v$.$touch();
            this.$emit('valid-traffic-allocations', this.v$.$invalid === false);
          });
        }
      },
      immediate: true,
    },

    championStrategy: {
      immediate: true,
      handler() {
        this.setDefaultStrategyDistributions();
      },
    },
  },

  async created() {
    await this.fetchModels();
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    async fetchModels() {
      try {
        this.isLoading = true;
        await Promise.all([this.getScoringModels(), this.getProductAssignmentModels()]);
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Failed to fetch scoring models or product assignment models',
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * APIs paginate (scoring-model max perPage 100; product-assignment-model max 250).
     * Fetch every page so dropdowns include all models (e.g. V108, not only first page).
     */
    async fetchAllPages(url, perPage) {
      const { data: firstBody } = await this.$axios.get(url, {
        params: { perPage, page: 1 },
      });
      const firstItems = Array.isArray(firstBody?.data) ? firstBody.data : [];
      const lastPage = Math.max(
        1,
        Number(firstBody?.last_page ?? firstBody?.meta?.last_page ?? 1) || 1,
      );

      if (lastPage <= 1) {
        return firstItems;
      }

      const rest = await Promise.all(
        Array.from({ length: lastPage - 1 }, (_, i) => this.$axios.get(url, {
          params: { perPage, page: i + 2 },
        })),
      );

      const aggregated = [...firstItems];
      rest.forEach(({ data: body }) => {
        const items = Array.isArray(body?.data) ? body.data : [];
        aggregated.push(...items);
      });

      return aggregated;
    },

    async getScoringModels() {
      this.scoringModels = await this.fetchAllPages('api/v1/scoring-model', 100);
    },

    async getProductAssignmentModels() {
      this.productAssignmentModels = await this.fetchAllPages(
        'api/v1/product-assignment-model',
        250,
      );
    },

    isReadOnlyStrategy(strategy) {
      return Boolean(strategy?.isChampionStrategy);
    },

    buildStrategyRow({ isChampionStrategy = false, strategy = null, allocationPct = 0 } = {}) {
      return {
        isChampionStrategy,
        strategyId: strategy?.id ?? null,
        strategyName: strategy ? `${strategy.name}${isChampionStrategy ? ' (Champion)' : ''}` : '',
        strategyDescription: strategy?.description ?? '',
        scoringModelId: strategy?.scoring_mo_id ?? null,
        productAssignmentModelId: strategy?.prod_asgmt_id ?? null,
        allocationPct: Number(allocationPct),
      };
    },

    setDefaultStrategyDistributions() {
      if (!this.championStrategy) {
        return;
      }

      const defaultRow = this.buildStrategyRow({
        isChampionStrategy: true,
        strategy: this.championStrategy,
        allocationPct: 100,
      });

      const existingDefaultIndex = this.trafficAllocations
        ?.findIndex((allocation) => allocation.isChampionStrategy);

      if (existingDefaultIndex !== -1) {
        const existing = this.trafficAllocations[existingDefaultIndex];
        // eslint-disable-next-line eqeqeq -- coerce API string/number strategy ids
        if (existing.strategyId != this.championStrategy.id) {
          this.trafficAllocations.splice(existingDefaultIndex, 1, {
            ...existing,
            ...defaultRow,
          });
        }
        return;
      }

      this.trafficAllocations = [defaultRow];
    },

    addStrategy() {
      this.trafficAllocations.push(this.buildStrategyRow());
    },

    canBeDeleted(index) {
      // Do not allow deletion of strategies that have been saved.
      return !this.trafficAllocations[index]?.strategyId;
    },

    removeStrategy(index) {
      if (index >= 0 && index < this.trafficAllocations.length) {
        this.trafficAllocations.splice(index, 1);
      }
    },

    getFieldError(fieldName, index) {
      const eachRule = this.v$.trafficAllocations?.$each;
      const rawResponse = eachRule?.$response;
      const response = unref(rawResponse) ?? rawResponse;
      if (eachRule?.$dirty && eachRule?.$invalid && response?.$errors?.[index]?.[fieldName]?.length) {
        return response.$errors[index][fieldName]
          .map((e) => e.$message)
          .filter(Boolean);
      }

      const serverErrorKey = `strategy_allocations.${index}.${fieldName}`;

      if (this.serverErrors && this.serverErrors[serverErrorKey]) {
        return this.serverErrors[serverErrorKey];
      }

      return [];
    },
  },
};
</script>
